import app from '../app';
import { TenantType, UserType } from '../types';
import { refreshToken } from './oauth';

/**
 * Gets the current authenticated user
 * Reuses existing app.session Backbone.Model for state management
 * Automatically attempts token refresh if authentication fails with 401
 * @returns {Promise<UserType>} The current user object
 * @throws {Error} If not authenticated or API request fails after refresh attempt
 */
export async function getCurrentUser(): Promise<UserType> {
  // If session already has user data, return it
  const existingUser = app.session.get('user');
  if (existingUser && app.session.get('logged_in')) {
    return existingUser as UserType;
  }

  try {
    // Try to fetch from backend (app.session.check_auth does fetch + store)
    await app.session.check_auth();
  } catch (error) {
    // If 401 Unauthorized, try refreshing token before giving up
    // Check for 401 status in error (works for both Axios and jQuery AJAX)
    const status = error?.response?.status || error?.status;
    if (status === 401) {
      try {
        // Attempt to refresh the token
        await refreshToken();
        // Retry authentication after refresh
        await app.session.check_auth();
      } catch (refreshError) {
        // Refresh failed - user needs to re-authenticate
        throw error; // Throw original error to trigger OAuth redirect
      }
    } else {
      // Not a 401 or refresh failed - rethrow original error
      throw error;
    }
  }

  const user = app.session.get('user');
  if (!user) {
    throw new Error('User data not available');
  }

  return user as UserType;
}

/**
 * Gets the current tenant/organization
 * Reuses existing app.session Backbone.Model for state management
 * Automatically attempts token refresh if authentication fails with 401
 * @returns {Promise<TenantType>} The current tenant object
 * @throws {Error} If not authenticated or API request fails after refresh attempt
 */
export async function getCurrentTenant(): Promise<TenantType> {
  // If session already has tenant data, return it
  const existingTenant = app.session.get('tenant');
  if (existingTenant && app.session.get('logged_in')) {
    return existingTenant as TenantType;
  }

  try {
    // Try to fetch from backend (app.session.check_auth does fetch + store)
    await app.session.check_auth();
  } catch (error) {
    // If 401 Unauthorized, try refreshing token before giving up
    // Check for 401 status in error (works for both Axios and jQuery AJAX)
    const status = error?.response?.status || error?.status;
    if (status === 401) {
      try {
        // Attempt to refresh the token
        await refreshToken();
        // Retry authentication after refresh
        await app.session.check_auth();
      } catch (refreshError) {
        // Refresh failed - user needs to re-authenticate
        throw error; // Throw original error to trigger OAuth redirect
      }
    } else {
      // Not a 401 or refresh failed - rethrow original error
      throw error;
    }
  }

  const tenant = app.session.get('tenant');
  if (!tenant) {
    throw new Error('Tenant data not available');
  }

  return tenant as TenantType;
}

/**
 * Clears the session data
 * Used on logout to ensure fresh data is fetched on next authentication
 */
export function clearCache() {
  app.session.clear();
}

/**
 * Invalidates the session and forces a fresh fetch on next access
 * Alias for clearCache() for semantic clarity
 */
export function invalidateCache() {
  clearCache();
}
